// §4.8's FLOOR VERSION — /floor/quality/scoreboard (spec 07 §4.8: "floor
// version shows only the shift scoreboard for the worker's facility"; built
// by review F16 under the completeness rule). READ-ONLY over the same §5
// endpoint the office panel uses: floor_worker's cell covers exactly this
// route (review F6's server narrowing), their RLS fence scopes the answer to
// their own facility, and no value field exists on the shape at all.
//
// The facility is the WORKER'S OWN, resolved from the org read — a
// floor_worker's RESTRICTIVE fence returns only theirs, so the first row IS
// their plant; no picker is offered because there is nothing to pick.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import type { components } from "@assessly/api-client";
import { client } from "../../../lib/api";
import { facilityTodayIso } from "../../../lib/business-dates";
import { useTenantTimezone } from "../../../lib/role-codes";
import { unwrap } from "../../../lib/request";
import {
  FloorEmpty,
  FloorError,
  FloorNeedsConnection,
  FloorScreen,
  FloorSkeleton,
  SnapshotNote,
} from "../components/floor-chrome";
import { floorReadSignal, useCachedRead } from "../hooks/use-cached-read";

type ScoreboardRow = components["schemas"]["QualityShiftScoreboardRow"];

export function FloorScoreboardScreen() {
  const { t } = useTranslation();
  // The facility's today (D-089), never the UTC date: from 00:00 to 05:00
  // in Almaty the UTC date is yesterday, and the board opened on the
  // previous day's shifts (D-600). A date the operator picks wins.
  const timezone = useTenantTimezone();
  const [picked, setDate] = useState<string | undefined>(undefined);
  const date = picked ?? facilityTodayIso(Date.now(), timezone);

  const board = useCachedRead<{ facilityName: string; rows: readonly ScoreboardRow[] }>({
    queryKey: ["floor", "quality-scoreboard", date],
    cacheKey: `quality-scoreboard:${date}`,
    queryFn: async ({ signal }) => {
      const facilities = unwrap(
        await client.GET("/v1/org/facilities", {
          params: { query: { status: "active" } },
          signal: floorReadSignal(signal),
        }),
        "facilities",
      );
      const mine = facilities.items[0];
      if (mine === undefined) throw new Error("no facility visible to this session");
      const res = unwrap(
        await client.GET("/v1/quality/analytics/shift-scoreboard", {
          params: { query: { date, facility_id: mine.id } },
          signal: floorReadSignal(signal),
        }),
        "quality-scoreboard",
      );
      return { facilityName: mine.name, rows: res.rows };
    },
  });

  const rows = board.data?.rows ?? [];

  return (
    <FloorScreen
      title={t("quality.floor.scoreboardTitle")}
      back={
        <Link to="/floor" className="floor-back" data-testid="floor-scoreboard-back">
          {t("quality.floor.backHome")}
        </Link>
      }
    >
      <SnapshotNote
        source={board.source}
        cachedAt={board.cachedAt}
        revalidating={board.revalidating}
      />

      <div className="floor-scoreboard-controls">
        <label className="floor-field">
          <span>{t("quality.floor.scoreboardDate")}</span>
          <input
            type="date"
            value={date}
            data-testid="floor-scoreboard-date"
            onChange={(e) => {
              setDate(e.target.value);
            }}
          />
        </label>
        {board.data !== undefined ? (
          <p className="floor-scoreboard-facility" data-testid="floor-scoreboard-facility">
            {board.data.facilityName}
          </p>
        ) : null}
      </div>

      {board.isLoading && rows.length === 0 ? <FloorSkeleton rows={3} /> : null}

      {board.source === "none" && !board.online ? (
        <FloorNeedsConnection what={t("quality.floor.scoreboardThing")} />
      ) : null}

      {board.error !== null && board.source === "none" && board.online ? (
        <FloorError
          title={t("quality.floor.scoreboardError")}
          detail={t("quality.floor.scoreboardErrorBody")}
        />
      ) : null}

      {!board.isLoading && board.error === null && rows.length === 0 ? (
        <FloorEmpty
          title={t("quality.floor.scoreboardEmptyTitle")}
          body={t("quality.floor.scoreboardEmptyBody")}
        />
      ) : null}

      {rows.length > 0 ? (
        <ul className="floor-scoreboard-list" data-testid="floor-scoreboard-rows">
          {rows.map((row) => (
            <li key={row.shift_instance_id ?? "unattributed"} className="floor-scoreboard-row">
              <p className="floor-scoreboard-shift">
                {row.shift_label ?? t("quality.analytics.unattributed")}
              </p>
              <dl className="floor-scoreboard-facts">
                <div>
                  <dt>{t("quality.analytics.colInspections")}</dt>
                  <dd>{row.inspections}</dd>
                </div>
                <div>
                  <dt>{t("quality.analytics.colDefectsProduced")}</dt>
                  <dd>{row.defects_produced}</dd>
                </div>
                <div>
                  <dt>{t("quality.analytics.colDefectsCaught")}</dt>
                  <dd>{row.defects_caught}</dd>
                </div>
                <div>
                  <dt>{t("quality.analytics.colHandoffs")}</dt>
                  <dd>{row.handoff_incidents}</dd>
                </div>
                <div>
                  <dt>{t("quality.analytics.fpyLabel")}</dt>
                  <dd>{row.fpy ?? "—"}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      ) : null}
    </FloorScreen>
  );
}
